console.log('products-per-page.js v4 loaded');

class ProductsPerPage extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    if (this.select) this.select.addEventListener('change', this.onChange.bind(this));
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onChange(event) {
    const searchParams = new URLSearchParams(window.location.search);

    // "view", not "per_page": Shopify's page cache ignores parameters it does
    // not recognise, so every per_page value was served the same cached page.
    searchParams.set('view', event.target.value);

    // A different page size makes the current page number meaningless, and
    // section_id must never leak into the address bar. per_page is what this
    // feature used to use - clear it off any link that still carries it.
    searchParams.delete('page');
    searchParams.delete('section_id');
    searchParams.delete('per_page');

    this.renderPage(searchParams.toString(), event.target.value);
    try{iWish&&setTimeout(()=>iWish.checkIwishColl(),2000)}catch(e){}
  }

  renderPage(searchParams, requested) {
    this.toggleLoading(true);

    // A slower earlier request must never overwrite a newer one.
    const requestId = (this.requestId || 0) + 1;
    this.requestId = requestId;

    // Deliberately NOT the Section Rendering API (?section_id=...). The section
    // has to read per_page out of content_for_header, and in a section request
    // the URL held there is the page the request came from, not the URL being
    // requested - so the server kept rendering the previously selected size.
    // A normal page request carries its own URL, so what comes back is always
    // the size that was asked for. The whole document is fetched; only the grid
    // and the product count are taken from it.
    const url = `${window.location.pathname}?${searchParams}`;

    fetch(url, { cache: 'no-store' })
      .then((response) => response.text())
      .then((html) => {
        if (requestId !== this.requestId) return;

        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const swapped = ProductsPerPage.renderProductGridContainer(parsedHTML);
        ProductsPerPage.renderProductCount(parsedHTML);
        this.syncSelection(parsedHTML, requested);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        ProductsPerPage.updateURLHash(searchParams);
        ProductsPerPage.report(url, requested, parsedHTML, swapped);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (requestId === this.requestId) this.toggleLoading(false);
      });
  }

  // Temporary instrumentation. One line per change, enough to say whether the
  // server honoured per_page and whether the new markup reached the page.
  static report(url, requested, parsedHTML, swapped) {
    const rendered = parsedHTML.querySelector('products-per-page select');
    const inResponse = parsedHTML.querySelectorAll('#product-grid > li').length;
    const inDom = document.querySelectorAll('#product-grid > li').length;

    // console.log(
    //   `products-per-page v4 | requested=${requested} serverRendered=${rendered ? rendered.value : "MISSING"} ` +
    //     `swapped=${swapped} productsInResponse=${inResponse} productsInDom=${inDom}`,
    // );
    // console.log('products-per-page v4 | url:', url);
  }

  // The selector sits outside #ProductGridContainer, so it isn't swapped in with
  // the rest of the markup. Fall back to the value the server actually resolved
  // so the dropdown can never claim a page size the grid isn't using.
  syncSelection(parsedHTML, requested) {
    const rendered = parsedHTML.querySelector('products-per-page select');
    if (!rendered || !this.select) return;

    // If this ever fires, the section did not read per_page from the request -
    // a server-side problem, not a rendering one. Say so plainly rather than
    // letting the grid quietly disagree with the URL.
    if (requested && rendered.value !== requested) {
      console.warn(
        `products-per-page: requested "${requested}" but the section rendered "${rendered.value}".`,
      );
    }

    if (rendered.value !== this.select.value) this.select.value = rendered.value;
  }

  toggleLoading(isLoading) {
    if (this.select) this.select.disabled = isLoading;

    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);

    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((element) => {
      element.classList.toggle('loading', isLoading);
    });

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));
  }

  static renderProductGridContainer(parsedHTML) {
    const container = document.getElementById('ProductGridContainer');
    const source = parsedHTML.getElementById('ProductGridContainer');

    // Returning quietly here used to make a failed swap look exactly like a
    // successful one: the address bar moved on while the old grid stayed put.
    if (!container) {
      console.error('products-per-page: no #ProductGridContainer in the page.');
      return false;
    }
    if (!source) {
      console.error('products-per-page: the response contained no #ProductGridContainer.');
      return false;
    }

    container.innerHTML = source.innerHTML;
    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    return true;
  }

  static renderProductCount(parsedHTML) {
    const sourceCount = parsedHTML.getElementById('ProductCount');
    if (!sourceCount) return;

    const container = document.getElementById('ProductCount');
    if (container) {
      container.innerHTML = sourceCount.innerHTML;
      container.dataset.productCount = sourceCount.dataset.productCount || '';
      container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    }

    document.querySelectorAll('#ProductCountDesktop').forEach((element) => {
      element.innerHTML = sourceCount.innerHTML;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);

    // Keep the facets history listener in sync so the back button still works.
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsPrev = searchParams;
    }
  }
}

customElements.define('products-per-page', ProductsPerPage);

// Rendered at the end of the grid when "Show all" is selected. Each time it
// scrolls into view it pulls the next batch of products in and appends them, so
// "Show all" really does reach every product even though a single paginate call
// can only return 50.
class ProductGridInfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '400px' },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }

  get nextUrl() {
    return this.dataset.nextUrl;
  }

  loadNext() {
    if (this.isLoading || !this.nextUrl) return;

    this.isLoading = true;


    fetch(this.nextUrl, { cache: 'no-store' })
      .then((response) => response.text())
      .then((html) => {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        this.appendProducts(parsedHTML);
        this.advance(parsedHTML);
      })
      .catch((error) => {
        console.error(error);
        // Stop observing rather than retrying on every scroll tick.
        this.observer?.disconnect();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  appendProducts(parsedHTML) {
    const grid = document.getElementById('product-grid');
    const source = parsedHTML.getElementById('product-grid');
    if (!grid || !source) return;

    const items = Array.from(source.children);
    items.forEach((item) => {
      item.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    });

    grid.append(...items);
  }

  advance(parsedHTML) {
    try{iWish&&setTimeout(()=>iWish.checkIwishColl(),2000)}catch(e){}
    const next = parsedHTML.querySelector('product-grid-infinite-scroll');

    if (!next?.dataset.nextUrl) {
      this.observer?.disconnect();
      this.remove();
      return;
    }

    this.dataset.nextUrl = next.dataset.nextUrl;

    // Appending products pushes this element down the page. If it happens to
    // still be within view the observer won't fire again on its own, so
    // re-observe to force a fresh intersection check.
    this.observer?.unobserve(this);
    this.observer?.observe(this);
  }
}

customElements.define('product-grid-infinite-scroll', ProductGridInfiniteScroll);
